import { Injector } from "@angular/core";

import type { IBaseCard, TranslateObject } from "@app/views-interfaces";
import { IFirmRolesService } from "@shared/ifirm-roles-service";
import { UsersService } from "@shared/users-service";
import { CardsService } from "@services/cards.service";
import { SimpleFilterMulti } from "./types/simple-filter-multi";
import type { FilterOption, FilterMultiSelection, Predicate } from "@filter/filter.types";

export abstract class RoleFilterService<TItem extends IBaseCard> extends SimpleFilterMulti<TItem> {
    protected abstract readonly injector: Injector;
    private rolesService?: IFirmRolesService;
    protected getRolesService(): IFirmRolesService {
        if (!this.injector) throw new Error("Injector is not available");
        return (this.rolesService ??= this.injector.get(IFirmRolesService));
    }

    private usersService?: UsersService;
    protected getUsersService(): UsersService {
        if (!this.injector) throw new Error("Injector is not available");
        return (this.usersService ??= this.injector.get(UsersService));
    }

    private cardsService?: CardsService;
    protected getCardsService(): CardsService {
        if (!this.injector) throw new Error("Injector is not available");
        return (this.cardsService ??= this.injector.get(CardsService));
    }

    protected noneOption: FilterOption | null = { value: null, label: "General.None" };

    protected getPredicate(selection: FilterMultiSelection): Predicate<TItem> {
        return (item) => {
            const value = this.getValue(item);
            if (value !== undefined && value && selection.values.includes(value)) return true;

            const otherValue = this.usersService?.getUserById(value as string)?.Id;
            if (otherValue !== undefined && otherValue && selection.values.includes(otherValue)) return true;

            if (!this.noneOption || !selection.values.includes(this.noneOption.value)) return false;

            const finalValue = !value ? value : this.getLabel(value);

            return !finalValue && selection.values.includes(this.noneOption.value);
        };
    }
}

export abstract class RoleUserFilterService<TItem extends IBaseCard> extends RoleFilterService<TItem> {
    protected abstract roleId: string;

    protected getLabel(value: string): string | undefined | null | TranslateObject {
        return this.getRolesService().getUserNameById(this.roleId, value);
    }

    protected getAllValues(): readonly string[] {
        const rolesService = this.getRolesService();
        const users =
            (typeof rolesService.rolesWithUsers === "function"
                ? rolesService.rolesWithUsers().get(this.roleId)?.Users
                : rolesService.getRoleById(this.roleId)?.Users) ?? [];
        return Array.from(users.values() ?? [])
            .filter(
                (user) =>
                    user.Active ||
                    user.Active === undefined ||
                    this.getCardsService().getCardsCountForUser(user).totalByRole[this.roleId] > 0,
            )
            .map((user) => user.IFirmUserId ?? user.Id);
    }
}
